import firebaseService from '../firebase/FirebaseService';
// import firebase from "firebase/app";
import "firebase/auth";

/**
 * Añade un studiante a un grupo
 * @param userId id de usuario proveniente del sistema de authenticación 
 * @param groupId id de grupo
 */
export const addStudentToGroup = async (userId: String, groupId: String) => {
    const junctionRef = firebaseService.db?.doc(`junction_group_student/${groupId}_${userId}`);
    await junctionRef?.set({ groupId, studentId: userId })
}

/**
 * Añade un actividad a un grupo
 * @param activityId id de la actividad
 * @param groupId id del grupo
 */
export const addActivityToGroup = async (activityId: String, groupId: String) => {
    const junctionRef = firebaseService.db?.doc(`junction_activity_group/${activityId}_${groupId}`);
    await junctionRef?.set({ activityId, groupId })
}

/**
 * Remueve una actividad de un grupo
 * @param activityId id de la actividad
 * @param groupId id del grupo
 */
export const removeActivityOfGroup = async (activityId: string, groupId: string) => {
    const junctionRef = firebaseService.db?.doc(`junction_activity_group/${activityId}_${groupId}`);
    junctionRef?.delete();
}

/**
 * Remueve un usuario de un grupo
 * @param userId id de usuario 
 * @param groupId id de grupo
 */
export const removeStudentFromGroup = async (userId: String, groupId: String) => {
    const junctionRef = firebaseService.db?.doc(`junction_group_student/${groupId}_${userId}`);
    await junctionRef?.delete();
}

/**
 * Marca una actividad como deleted
 * @param activityId id de actividad
 */
export const removeActivity = async (activityId: string) => {
    //La actividad no se borra, solo se le pone el atributo en deleted = true
    const activity = firebaseService.db?.doc(`activities/${activityId}`);
    if (activity) await activity.update({ deleted: true });
}

/**
 * Marca una grupo como deleted
 * @param groupId id de grupo
 */
export const removeGroup = async (groupId: string) => {
    //el grupo no se remueve, solo se le pone el atributo en deleted = true
    const group = firebaseService.db?.doc(`groups/${groupId}`);
    if (group) await group.update({ deleted: true });
}

/**
 * Consulta las actividades que tiene un grupo
 * @param groupId id de grupo
 * @returns arreglo de actividades
 */
export const getActivitiesOfGroup = async (groupId: string) => {
    const junctionActivityGroup = await firebaseService.db?.collection('junction_activity_group')
        .where('groupId', '==', groupId).get();

    if (junctionActivityGroup) {
        const result = await Promise.all(junctionActivityGroup?.docs.filter(doc => doc.exists).map(doc => firebaseService.db?.doc(`activities/${doc?.data().activityId}`).get()));
        return result.filter(doc => doc?.exists && !doc?.data()?.deleted);
    }

    return undefined;
}

/**
 * Consulta los estudiantes que tiene un grupo
 * @param groupId id de grupo
 * @returns arreglo con los grupos
 */
export const getStudentsOfGroup = async (groupId: string) => {
    const junctionGroupStudent = await firebaseService.db?.collection('junction_group_student')
        .where('groupId', '==', groupId).get();

    if (junctionGroupStudent) {
        const result = await Promise.all(junctionGroupStudent?.docs.filter(doc => doc.exists).map(doc => firebaseService.db?.doc(`users/${doc?.data().studentId}`).get()));
        return result
            .filter(doc => doc?.exists)
            .map((doc: any) => ({ id: doc.id, ...doc.data() }));
    }

    return [];
}

/**
 * Consulta los grupos que tiene un estudiante
 * @param userId id de usuario (estudiante)
 * @returns arreglo de grupos
 */
export const getGroupsOfStudent = async (userId: string) => {
    const junctionsGroupStudent = await firebaseService.db?.collection('junction_group_student')
        .where('studentId', '==', userId)
        .get();

    if (junctionsGroupStudent) {
        const result = await Promise.all(junctionsGroupStudent?.docs.filter(doc => doc.exists).map(doc => firebaseService.db?.doc(`groups/${doc?.data().groupId}`).get()));
        return result.filter(doc => doc?.exists && !doc?.data()?.deleted).map((doc: any) => ({ id: doc.id, ...doc.data() }));
    }
}

/**
 * Consulta los grupos que tiene una actividad
 * @param activityId id de actividad
 * @returns arreglo de grupos
 */
export const getGroupsOfActivity = async (activityId: string) => {
    const junctionActivityGroup = await firebaseService.db?.collection('junction_activity_group')
        .where('activityId', '==', activityId).get();

    if (junctionActivityGroup) {
        const result = await Promise.all(junctionActivityGroup?.docs.filter(doc => doc.exists).map(doc => firebaseService.db?.doc(`groups/${doc?.data().groupId}`).get()));
        // let a = result.filter(doc => doc?.exists && !doc?.data()?.deleted).map(doc => ({ id: doc?.id, ...doc?.data() }));

        return result
            .filter(doc => doc?.exists && !doc?.data()?.deleted)
            .map((doc: any) => ({ id: doc.id, ...doc.data() }));
    }

    return undefined;
}

/**
 * Si se pasó del limite de estudiantes, entonces el campo inscribed = students_limit
 * @param activityId id de actividad
 */
export const updateActivityInscribed = async (activityId: String) => {
    const activity = await firebaseService.getActivityById(activityId);
    let students_limit = activity?.students_limit ? activity?.students_limit : 0;
    if (activity.inscribed >= activity.students_limit)
        await firebaseService.updateActivity(activityId, { inscribed: students_limit });
}

/**
 * Añade un actividad a un estudiante
 * @param studentId id del estudiante
 * @param activityId id de la actividad
 */
export const addActivityToUser = async (studentId: String, activityId: String) => {
    const activity = await firebaseService.getActivityById(activityId);

    await firebaseService.updateActivityToUser(activityId);
    if (activity.inscribed >= activity.students_limit) {
        console.log("Sin lugares disponibles: " + activity.inscribed);
        updateActivityInscribed(activityId);
        return { type: "error", code: "full_group", message: "Esta actividad ya no tiene lugares disponibles" };
    }
    else {
        console.log("Con lugares disponibles: " + activity.inscribed);
        updateActivityInscribed(activityId);
        const junctionRef = firebaseService.db?.doc(`junction_student_activity/${studentId}_${activityId}`);
        await junctionRef?.set({ activityId, studentId });
        return { type: "success", code: "success", message: "You have successfully been enrolled in the activity" };
    }
}

/**
 * Remueve una actividad de un estudiante
 * @param studentId id del estudiante
 * @param activityId id de la actividad
 */
export const removeActivityFromUser = async (studentId: string, activityId: string) => {
    const junctionRef = firebaseService.db?.doc(`junction_student_activity/${studentId}_${activityId}`);
    await junctionRef?.delete();

    const activity = await firebaseService.getActivityById(activityId);
    let inscribed = activity?.inscribed ? activity?.inscribed : 0;
    inscribed = inscribed - 1 >= 0 ? inscribed - 1 : 0;
    await firebaseService.updateActivity(activityId, { inscribed: inscribed });

    return { type: "success", code: "success", message: "You have successfully been unenrolled from the activity" }
}

/**
 * Consulta las actividades que tiene un usuario
 * @param userId id de usuario (estudiante)
 * @returns arreglo de actividades
 */
export const getSubscribedActivitiesByUserId = async (userId: string) => {
    const junctionsStudentActivity = await firebaseService.db?.collection('junction_student_activity')
        .where('studentId', '==', userId)
        .get()
        .then((snapshot) => {
            const docs: any = []
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data(), subscribed: true })
            });

            return docs;
        });

    const activities = await Promise.all(junctionsStudentActivity.map((doc: any) => firebaseService.db?.doc(`activities/${doc?.activityId}`).get().then(snap => ({ id: snap.id, ...snap.data(), groupId: doc?.groupId }))));

    const cyclesIds: Array<string> = [];
    activities.forEach((element: any) => {
        if (!cyclesIds.includes(element?.cycle))
            cyclesIds.push(element?.cycle);
    });
    const cycles = await Promise.all(cyclesIds.map(cycleId => firebaseService.db?.doc(`cycles/${cycleId}`).get().then(snap => ({ id: snap.id, ...snap.data() }))));

    await Promise.all(activities.map(async (act: any) => {
        if (act?.image_ref)
            act.image_url = await firebaseService.getFile(act?.image_ref)
    }));

    return activities
        .filter((doc: any) => !doc?.deleted)
        .map((doc: any) => {
            const subscribed = junctionsStudentActivity ?
                junctionsStudentActivity.find((docSA: any) => docSA.studentId === userId && docSA.activityId === doc?.id) !== undefined :
                false;

            const cycle: any = cycles ?
                cycles.find((cyc) => cyc?.id === doc?.cycle) :
                {};

            return { id: doc?.id, ...doc, subscribed: subscribed, cycleData: cycle }
        });
}

export const getJunctionByStudentAndActivity = (userId: string, activityId: string) => {
    return firebaseService.db?.collection('junction_student_activity')
        .where('studentId', '==', userId)
        .where('activityId', '==', activityId)
        .get()
        .then((snapshot) => {
            const docs: any = []
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data() })
            });

            return docs.length > 0 ? docs[0] : undefined;
        });
}

/**
 * Consulta los alumnos inscritos a una actividad
 * @param activityId id de actividad
 * @returns arreglo de usuarios
 */
export const getUserByActivityId = async (activityId: string) => {
    const junctionsStudentActivity = await firebaseService.db?.collection('junction_student_activity')
        .where('activityId', '==', activityId)
        .get()
        .then((snapshot) => {
            const docs: any = []
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data() })
            });

            return docs;
        });

    let users = await Promise.all(junctionsStudentActivity.map((data: any) => firebaseService.db?.doc(`users/${data.studentId}`).get().then(snap => ({ ...snap.data() }))));

    return users;
}

export const getUsersByActivitiesObj = async (activity: any) => {
    let students: any = [];
    const junctionsStudentActivity = await firebaseService.db?.collection('junction_student_activity')
        .where('activityId', '==', activity.id)
        .get()
        .then((snapshot) => {
            const docs: any = []
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data() })
            });

            return docs;
        });

    await Promise.all(junctionsStudentActivity.map((data: any) => firebaseService.db?.doc(`users/${data.studentId}`).get().then((snap: any) => {
        students.push({
            activityId: activity.id,
            actName: activity.name,
            code: snap.data().code,
            displayName: snap.data().displayName,
            email: snap.data().email
        });
    })));

    return students;
}

/**
 * Consulta las actividades que tiene un usuario
 * @param userId id de usuario (estudiante)
 * @returns arreglo de actividades
 */
export const getActivitiesByUserId = async (userId: string) => {
    //TODO: Utilizar la nuevas funciones para que esta funcion realmente funcione.. "BIEN"
    // const groupsOfStudent = await getGroupsOfStudent(userId);
    // const activities = 
    const junctionsGroupStudent = await firebaseService.db?.collection('junction_group_student')
        .where('studentId', '==', userId)
        .get();

    const junctionsStudentActivity = await firebaseService.db?.collection('junction_student_activity')
        .where('studentId', '==', userId)
        .get()
        .then((snapshot) => {
            const docs: any = []
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data() })
            });

            return docs;
        });

    if (junctionsGroupStudent) {
        const junctionsActivityGroup = await Promise.all(junctionsGroupStudent?.docs.filter(doc => doc.exists)
            .map(doc => firebaseService.db?.collection('junction_activity_group')
                .where('groupId', '==', doc.data().groupId).get()));

        const activitiesGroup = junctionsActivityGroup.map(doc => doc?.docs.filter(doc => doc.exists)).flat();
        const activities = await Promise.all(activitiesGroup.map(doc => firebaseService.db?.doc(`activities/${doc?.data().activityId}`).get().then(snap => ({ id: snap.id, ...snap.data(), groupId: doc?.data().groupId }))));

        const groupsId: Array<string> = [];
        activitiesGroup.forEach(element => {
            if (!groupsId.includes(element?.data().groupId))
                groupsId.push(element?.data().groupId);
        });
        const groups = await Promise.all(groupsId.map(groupId => firebaseService.db?.doc(`groups/${groupId}`).get().then(snap => ({ id: snap.id, ...snap.data() }))));

        const cyclesIds: Array<string> = [];
        activities.forEach(async (doc: any) => {
            if (!cyclesIds.includes(doc?.cycle))
                cyclesIds.push(doc?.cycle);
        });
        const cycles = await Promise.all(cyclesIds.map(cycleId => firebaseService.db?.doc(`cycles/${cycleId}`).get().then(snap => ({ id: snap.id, ...snap.data() }))));

        await Promise.all(activities.map(async (act: any) => {
            if (act?.image_ref)
                act.image_url = await firebaseService.getFile(act?.image_ref)
        }));

        return activities
            .filter((doc: any) => !doc?.deleted)
            .map((doc: any) => {
                const subscribed = junctionsStudentActivity ?
                    junctionsStudentActivity.find((docSA: any) => docSA.studentId === userId && docSA.activityId === doc?.id) !== undefined :
                    false;

                const group: any = groups ?
                    groups.find((grp) => grp?.id === doc?.groupId) :
                    {};

                const cycle: any = cycles ?
                    cycles.find((cyc) => cyc?.id === doc?.cycle) :
                    {};

                return { ...doc, subscribed: subscribed, start_date: group?.start_date, end_date: group?.end_date, cycleData: cycle }
            });
    }

    return undefined;
}

interface User {
    activities: boolean;
    success: boolean;
    email: string | null;
    displayName: string | null;
    phoneNumber: string | null;
    photoURL: string | null;
    providerId: string;
    code: string | undefined;
    admin?: boolean;
    teacher?: boolean;
    student?: boolean;
    deleted?: boolean;
}

export const registerUserToDatabase = async () => {
    if (firebaseService.auth?.currentUser) {
        const currentUser = firebaseService.auth?.currentUser;
        const code = currentUser.email?.substring(0, currentUser.email.lastIndexOf("@"));
        const domain = currentUser.email?.substring(currentUser.email.lastIndexOf("@") + 1);
        const user: User = {
            email: currentUser.email,
            displayName: currentUser.displayName,
            phoneNumber: currentUser.phoneNumber,
            photoURL: currentUser.photoURL,
            providerId: currentUser.providerId,
            code: code,
            deleted: false,
            success: true,
            activities: true
        }

        const userDB = await firebaseService.getUserById(currentUser.email);

        if (userDB.deleted)
            return;

        const junctionsGroupStudent = await firebaseService.db?.collection('junction_group_student')
            .where('studentId', '==', user.email)
            .get();

        const groupsPromises: any = await junctionsGroupStudent?.docs.map((doc) => new Promise((resolve, reject) => {
            firebaseService.db?.doc(`groups/${doc?.data().groupId}`).get().then(snap => {
                resolve({ id: snap.id, ...snap.data() });
            })
        }));

        const groups = await Promise.all(groupsPromises);

        groups.forEach((group: any) => {
            if (group?.name === "users_admin")
                user.admin = true;

            if (group?.name === "users_students")
                user.student = true;

            if (group?.name === "users_teacher")
                user.teacher = true;
        });
        if (domain !== 'marstom.com' && domain !== 'cervantes.edu.mx') {
            user.success = false;
        } else {
            const userRef = firebaseService.db?.doc(`users/${user.email}`);
            userRef?.set(user);

        }// con este if se evita que se agregen los email a la bd de firebase


        //si el usuario no concuerda con el dominio de marstom, no podra entrar, en el caso del colegio, será el correo del colegio.. 
        //o quiza solo poner una pantalla de que este usuario no es válido
        // if (domain !== 'marstom.com') {
        //     // currentUser.delete();
        //     return;
        // } else {


        return user;
        // }
    } else {
        return;
    }
}
